import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeForm = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState(null);
  const [formData, setFormData] = useState({
    // Medical History
    current_medications: '',
    past_medications: '',
    medical_conditions: '',
    allergies: '',
    // Mental Health History
    previous_therapy: '',
    previous_diagnosis: '',
    hospitalization_history: '',
    family_mental_health: '',
    // Current Situation
    current_symptoms: '',
    symptom_duration: '',
    triggers: '',
    coping_mechanisms: '',
    // Goals
    therapy_goals: '',
    expectations: '',
    // Lifestyle
    sleep_pattern: '',
    exercise_routine: '',
    substance_use: '',
    support_system: '',
    // Emergency
    suicidal_thoughts: '',
    self_harm: '',
    emergency_plan: ''
  });

  const fetchClientInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/intake-form/${bookingId}`);
      if (response.data.error) {
        toast.error('Invalid booking link');
        navigate('/');
      } else {
        setClientInfo(response.data);
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load booking information');
      navigate('/');
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    document.title = 'Client Intake Form - MIND FRAME';
    fetchClientInfo();
  }, [fetchClientInfo]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/intake-form`, {
        booking_id: bookingId,
        ...formData
      });
      
      if (response.data.status === 'success') {
        toast.success('Intake form submitted successfully!');
        setTimeout(() => navigate('/'), 2000);
      } else {
        toast.error('Failed to submit form');
      }
    } catch (error) {
      toast.error('Error submitting form');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">MIND FRAME</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-700 mb-2">Client Intake Form</h2>
          <p className="text-slate-600">Welcome, {clientInfo?.name}! Please complete this form before your first session.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Medical History */}
          <Card>
            <CardHeader>
              <CardTitle>Medical History</CardTitle>
              <CardDescription>Information about your physical health</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_medications">Current Medications (if any)</Label>
                <Textarea id="current_medications" name="current_medications" value={formData.current_medications} onChange={handleInputChange} placeholder="List any medications you're currently taking" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="past_medications">Past Medications</Label>
                <Textarea id="past_medications" name="past_medications" value={formData.past_medications} onChange={handleInputChange} placeholder="Medications you've taken in the past" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="medical_conditions">Medical Conditions</Label>
                <Textarea id="medical_conditions" name="medical_conditions" value={formData.medical_conditions} onChange={handleInputChange} placeholder="Any diagnosed medical conditions" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Input id="allergies" name="allergies" value={formData.allergies} onChange={handleInputChange} placeholder="Any known allergies" />
              </div>
            </CardContent>
          </Card>

          {/* Mental Health History */}
          <Card>
            <CardHeader>
              <CardTitle>Mental Health History</CardTitle>
              <CardDescription>Your previous mental health experiences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="previous_therapy">Previous Therapy/Counseling Experience</Label>
                <Textarea id="previous_therapy" name="previous_therapy" value={formData.previous_therapy} onChange={handleInputChange} placeholder="Have you seen a therapist before? When and for how long?" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="previous_diagnosis">Previous Mental Health Diagnoses</Label>
                <Textarea id="previous_diagnosis" name="previous_diagnosis" value={formData.previous_diagnosis} onChange={handleInputChange} placeholder="Any previous diagnoses (e.g., anxiety, depression, etc.)" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hospitalization_history">Psychiatric Hospitalization History</Label>
                <Textarea id="hospitalization_history" name="hospitalization_history" value={formData.hospitalization_history} onChange={handleInputChange} placeholder="Any past hospitalizations related to mental health" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="family_mental_health">Family Mental Health History</Label>
                <Textarea id="family_mental_health" name="family_mental_health" value={formData.family_mental_health} onChange={handleInputChange} placeholder="Mental health conditions in your family" rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* Current Situation */}
          <Card>
            <CardHeader>
              <CardTitle>Current Situation</CardTitle>
              <CardDescription>Tell us about what brings you here today</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_symptoms">Current Symptoms/Concerns *</Label>
                <Textarea id="current_symptoms" name="current_symptoms" value={formData.current_symptoms} onChange={handleInputChange} required placeholder="Describe what you're experiencing now" rows={4} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="symptom_duration">How Long Have You Experienced These Symptoms?</Label>
                <Input id="symptom_duration" name="symptom_duration" value={formData.symptom_duration} onChange={handleInputChange} placeholder="e.g., 6 months, 2 years, etc." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="triggers">Triggers or Patterns</Label>
                <Textarea id="triggers" name="triggers" value={formData.triggers} onChange={handleInputChange} placeholder="What situations or events make things worse?" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coping_mechanisms">Current Coping Strategies</Label>
                <Textarea id="coping_mechanisms" name="coping_mechanisms" value={formData.coping_mechanisms} onChange={handleInputChange} placeholder="What do you do to manage these feelings?" rows={3} />
              </div>
            </CardContent>
          </Card>

          {/* Goals and Expectations */}
          <Card>
            <CardHeader>
              <CardTitle>Goals and Expectations</CardTitle>
              <CardDescription>What you hope to achieve through counseling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="therapy_goals">What Are Your Goals for Therapy? *</Label>
                <Textarea id="therapy_goals" name="therapy_goals" value={formData.therapy_goals} onChange={handleInputChange} required placeholder="What would you like to work on or achieve?" rows={4} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expectations">Expectations from Counseling</Label>
                <Textarea id="expectations" name="expectations" value={formData.expectations} onChange={handleInputChange} placeholder="What do you expect from our sessions?" rows={3} />
              </div>
            </CardContent>
          </Card>

          {/* Lifestyle */}
          <Card>
            <CardHeader>
              <CardTitle>Lifestyle Information</CardTitle>
              <CardDescription>Your daily habits and routines</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sleep_pattern">Sleep Pattern</Label>
                <Textarea id="sleep_pattern" name="sleep_pattern" value={formData.sleep_pattern} onChange={handleInputChange} placeholder="How many hours? Quality of sleep?" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exercise_routine">Exercise Routine</Label>
                <Input id="exercise_routine" name="exercise_routine" value={formData.exercise_routine} onChange={handleInputChange} placeholder="Frequency and type of exercise" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="substance_use">Substance Use</Label>
                <Textarea id="substance_use" name="substance_use" value={formData.substance_use} onChange={handleInputChange} placeholder="Alcohol, tobacco, or other substances (frequency and amount)" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support_system">Support System</Label>
                <Textarea id="support_system" name="support_system" value={formData.support_system} onChange={handleInputChange} placeholder="Family, friends, or others you can rely on" rows={2} />
              </div>
            </CardContent>
          </Card>

          {/* Safety Assessment */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800">Safety Assessment</CardTitle>
              <CardDescription className="text-red-700">Important questions about your safety</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="suicidal_thoughts">Thoughts of Suicide or Self-Harm</Label>
                <Textarea id="suicidal_thoughts" name="suicidal_thoughts" value={formData.suicidal_thoughts} onChange={handleInputChange} placeholder="Have you had thoughts of harming yourself? (Please be honest - this helps us support you better)" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="self_harm">History of Self-Harm</Label>
                <Textarea id="self_harm" name="self_harm" value={formData.self_harm} onChange={handleInputChange} placeholder="Any past incidents of self-harm" rows={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_plan">Emergency Plan</Label>
                <Textarea id="emergency_plan" name="emergency_plan" value={formData.emergency_plan} onChange={handleInputChange} placeholder="Who would you contact in a crisis? What steps would you take?" rows={3} />
              </div>
              <div className="bg-white p-4 rounded-lg border border-red-300">
                <p className="text-sm text-red-800 font-semibold mb-2">If you're in crisis:</p>
                <p className="text-sm text-red-700">
                  Please call emergency services (112) or a mental health helpline immediately. 
                  Therapy is not a substitute for emergency care.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-lg py-6">
              Submit Intake Form
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntakeForm;
